import { Form } from "antd";
import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useQueryClient } from "react-query";

import {
  useAddNotification,
  useNotificationDetails,
  useNotificationUserCount,
  useUpdateNotification,
} from "../queries";
import Topbar from "../../../Topbar";
import EstimateTarget from "./EstimateTarget";
import GeneralDetails from "./GeneralDetails";
import ConditionalRules from "./ConditionalRules";
import { modifyRules, onDetailSuccess, getZipCodeToIdMap } from "../helper";
import { AUTHENTICATED_ROUTES } from "../../../../routes/constant";
import { CONDITIONAL_NOTIFICATION_TYPE, RULE_TYPE, CONDITION_TYPE, REDIRECTION_TYPE } from "../../../../constants/constantTypes";

import "./AddNotification.less";
import dateTimeService, {
  changeFormat,
  getDateTimeCST,
} from "../../../../util/date-time.service";

export default function AddNotificationContent(props) {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const history = useHistory();
  const { history: { location: { state: listInfo, pathname } = {} } = {} } =
    props;

  const {
    mutateAsync: getNotificationCount,
    isLoading: notificationCountLoader,
    data: notificationCountDetials,
  } = useNotificationUserCount();

  const { data: notificationDetails = {}, isLoading: detailLoader } =
    useNotificationDetails({
      onSuccess: (res) => console.log(),
      id: listInfo?.id,
    });

  useEffect(() => {
    if (Object.keys(notificationDetails)?.length) {
      onDetailSuccess(notificationDetails, form, getNotificationCount, queryClient);
    }
  }, [notificationDetails]);

  const { mutateAsync: createNotification, isLoading: addNotificationLoader } =
    useAddNotification({
      onSuccess: onSuccess,
    });

  const { mutate: updateNotification, isLoading: updateNotificationLoading } =
    useUpdateNotification({
      onSuccess: onSuccess,
    });

  function onSuccess() {
    queryClient.removeQueries("notificationManagement");
    if (listInfo?.id) {
      queryClient.removeQueries(
        "notificationManagement",
        "details",
        listInfo?.id
      );
    }
    history.push(AUTHENTICATED_ROUTES.PUSH_MANAGEMENT);
  }
  
  function handleFinish(values) {
    const allValues = form.getFieldsValue(true);
    const {
      activityDayId,
      freebieId,
      postId,
      exploreId,
      accessId,
      activityId,
      recommendConnectPartnerId,
      redirectionType,
      isDeepLink,
      isInAppRedirection,
    } = allValues;
    const rules = form.getFieldValue("rules");

    let date;
    let modifiedRules;
    let zipCodes = [];
    if (values?.date?._d) {
      const startDate = changeFormat(values.date?._d);
      const startTime = changeFormat(
        values.time?._d,
        dateTimeService.DATE_TIME_FORMAT.TIME
      );
      date = getDateTimeCST(startDate, startTime);
    } else {
      date = null;
    }
    if (
      rules?.length &&
      values.isConditional === CONDITIONAL_NOTIFICATION_TYPE.YES
    ) {
      // Get neighborhood data from query client to create zipcode to ID mapping
      const zipCodeToIdMap = getZipCodeToIdMap(queryClient);

      // Extract zipcodes from neighborhood rules when condition is EQUAL (before conversion to IDs)
      if (rules) {
        rules.forEach((rule) => {
          if (
            rule?.ruleType === RULE_TYPE.NEIGHBOURHOOD &&
            rule?.conditionType === CONDITION_TYPE.EQUAL &&
            rule?.values &&
            Array.isArray(rule.values) &&
            rule.values.length > 0
          ) {
            // Extract zipcodes (neighborhood values are zipcodes as strings)
            zipCodes = [...zipCodes, ...rule.values.map(val => String(val))];
          }
        });
      }

      modifiedRules = Object.keys(rules[0])?.length
        ? modifyRules(rules, zipCodeToIdMap)
        : null;
      
      values["rules"] = modifiedRules;
    }
    if (values?.isConditional) {
      values["isConditional"] =
        values?.isConditional === CONDITIONAL_NOTIFICATION_TYPE.YES
          ? true
          : false;
    }

    values["linkEnglish"] = values?.linkEnglish ? values?.linkEnglish : null;
    values["linkSpanish"] = values?.linkSpanish ? values?.linkSpanish : null;

    // Handle redirection type:
    // - Deep link or plain external link (no in-app redirection) => NONE (0)
    // - In-app redirection (Add Link) => use selected redirectionType
    if (isDeepLink || (!isInAppRedirection && !redirectionType)) {
      values["redirectionType"] = REDIRECTION_TYPE.NONE;
    } else if (redirectionType !== undefined && redirectionType !== null) {
      values["redirectionType"] = redirectionType;
    }

    // if (values?.rules) {
    //     modifiedRules = values?.rules?.map((item) => {
    //         if (item.values.indexOf(CONNECTED_TYPE.EMAIL) === -1) return item;
    //         return {
    //             ...item,
    //             values: [...item.values, CONNECTED_TYPE.PHONE, CONNECTED_TYPE.USER]
    //         };
    //     });
    // }

    if (listInfo?.id) {
      updateNotification({
        ...values,
        // ...(modifiedRules && { rules: modifiedRules }),
        id: listInfo?.id,
        date,
        ...(activityDayId && { activityDayId }),
        ...(freebieId && { freebieId }),
        ...(postId && { postId }),
        ...(exploreId && { exploreId }),
        ...(accessId && { accessId }),
        ...(recommendConnectPartnerId && { recommendConnectPartnerId }),
        ...(zipCodes.length > 0 && { zipCodes }),
      });
      return;
    }
    createNotification({
      ...values,
      // ...(modifiedRules && { rules: modifiedRules }),
      date,
      ...(activityDayId && { activityDayId }),
      ...(freebieId && { freebieId }),
      ...(postId && { postId }),
      ...(exploreId && { exploreId }),
      ...(accessId && { accessId }),
      ...(recommendConnectPartnerId && {
        recommendConnectPartnerId,
      }),
      ...(zipCodes.length > 0 && { zipCodes }),
    });
  }

  return (
    <>
      <Topbar title={`${listInfo?.id ? "Update" : "Add"} Notification`} />
      <div className="gx-main-content-wrapper">
        <Form
          name="notification"
          layout="vertical"
          initialValues={{ isConditional: 2 }}
          form={form}
          onFinish={handleFinish}
        >
          <Form.Item dependencies={["isConditional"]} noStyle>
            {({ getFieldValue }) => {
              const isConditional = getFieldValue("isConditional");
              const component =
                isConditional === CONDITIONAL_NOTIFICATION_TYPE.YES ? (
                  <EstimateTarget
                    form={form}
                    data={notificationCountDetials}
                    loading={notificationCountLoader}
                  />
                ) : null;
              return component;
            }}
          </Form.Item>

          <GeneralDetails
            form={form}
            loading={addNotificationLoader || updateNotificationLoading}
            detailsLoader={detailLoader}
          />
          <Form.Item dependencies={["isConditional"]} noStyle>
            {({ getFieldValue }) => {
              const isConditional = getFieldValue("isConditional");
              const component =
                isConditional === CONDITIONAL_NOTIFICATION_TYPE.YES ? (
                  <ConditionalRules
                    form={form}
                    loading={addNotificationLoader || updateNotificationLoading}
                    getNotificationCount={getNotificationCount}
                  />
                ) : null;
              return component;
            }}
          </Form.Item>
        </Form>
      </div>
    </>
  );
}
